import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './About.css';

gsap.registerPlugin(ScrollTrigger);

/**
 * About. Layout family: split editorial with a bordered aside.
 *
 * The numbers live in the hero's stat bars on the home page, so they are
 * only printed here when About is the whole page and the bars are absent.
 */
function About({ data, standalone = false }) {
  const { statement, philosophy, stats, focus } = data;
  const rootRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
        const tweens = [
          gsap.from('.about__heading', {
            scrollTrigger: { trigger: '.about__heading', start: 'top 88%' },
            opacity: 0, y: 24, duration: 0.8, ease: 'power3.out',
          }),
          gsap.from('.about__prose > *', {
            scrollTrigger: { trigger: '.about__prose', start: 'top 88%' },
            opacity: 0, y: 20, duration: 0.75, stagger: 0.1, ease: 'power3.out',
          }),
          gsap.from('.about__focus li', {
            scrollTrigger: { trigger: '.about__aside', start: 'top 90%' },
            opacity: 0, x: -12, duration: 0.55, stagger: 0.06, ease: 'power3.out',
          }),
        ];
        return () => tweens.forEach((t) => t.kill());
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      {standalone && (
        <Helmet>
          <title>About, Damilare Oyedeji</title>
          <meta
            name="description"
            content="Software engineer with six years building scalable systems and fluid interfaces across fintech, edtech and SaaS."
          />
        </Helmet>
      )}

      <section className="about act act--ruled" id="about" ref={rootRef}>
        <div className="shell">

          <header className="about__head">
            <span className="eyebrow">Meet damilare</span>
            <h2 className="about__heading">
              I build things that hold up <span className="accent">under load</span>.
            </h2>
          </header>

          <div className="about__split">
            <div className="about__prose">
              <p>{statement}</p>
              <p>{philosophy}</p>
            </div>

            <aside className="about__aside">
              <span className="about__aside-label">What I do</span>
              <ul className="about__focus">
                {focus.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </aside>
          </div>

          {standalone && (
            <ul className="about__stats">
              {stats.map((s) => (
                <li className="figure" key={s.label}>
                  <span className="figure__value">{s.value}</span>
                  <span className="figure__label">{s.label}</span>
                </li>
              ))}
            </ul>
          )}

        </div>
      </section>
    </>
  );
}

export default About;
